<template>
  <div v-if="showModal">
    <div :class="$style.modalWrapper" role="region" aria-label="Cookie Consent notice">
      <div :class="$style.modal">
        <div :class="$style.modalHeader">
          <button aria-label="Dismiss cookie banner" :class="$style.btnDismiss" @click="dismissBanner" @keydown.enter="dismissBanner" tabindex="0" v-if="!privacySettings.askCookieConsent"></button>
          <h1 :class="$style.modalTitle">{{ i18n.modal.title }}</h1>
          <p :class="$style.text">{{ i18n.modal.notice }}</p>          
        </div>
        <div :class="$style.modalBody">
          <div :class="necessaryClassnames">
            <h2 :class="$style.groupTitle">{{ i18n.modal['necessary-title'] }}</h2>
            <p :class="$style.text">{{ i18n.modal['necessary-text'] }}</p>
            <a :class="cookiesLinkClassname" data-testid="button-toggle-necessary-cookies" @click="toggleNecessaryCookies" @keydown.enter="toggleNecessaryCookies">{{ showNecessaryCookies ? i18n['less-details'] : i18n['more-details'] }}</a>
            <div v-if="showNecessaryCookies" :class="$style.cookieList">
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Cookie</span>
                  <span>laravel_session & XSRF-TOKEN</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>2 {{ i18n.cookies.duration.hours }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies.session }}</span>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>WebStorage</span>
                  <span>homerun-cookie-consent</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>{{ i18n.cookies.duration['no-expire'] }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies.consent }}</span>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>WebStorage</span>
                  <span>homerun-candidate-responses</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>{{ i18n.cookies.duration['expire-submission'] }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies.responses }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div :class="externalGroup">
            <h2 :class="$style.groupTitle">{{ i18n.modal['functional-title'] }}</h2>
            <p :class="$style.text">{{ i18n.modal['functional-text'] }}</p>
            <a :class="cookiesLinkClassname" data-testid="button-toggle-functional-cookies" @click="toggleFunctionalCookies" @keydown.enter="toggleFunctionalCookies">{{ showFunctionalCookies ? i18n['less-details'] : i18n['more-details'] }}</a>
            <div v-if="showFunctionalCookies" :class="$style.cookieList">
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Intercom</span>
                  <a href="https://www.intercom.com/help/en/articles/2361922-intercom-messenger-cookies" target="_blank">https://www.intercom.com/help/en/articles/2361922-intercom-messenger-cookies</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>YouTube</span>
                  <a href="https://policies.google.com/technologies/cookies/embedded?#types-of-cookies" target="_blank">https://policies.google.com/technologies/cookies/embedded?#types-of-cookies</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Google Maps</span>
                  <a href="https://policies.google.com/technologies/cookies/embedded?#types-of-cookies" target="_blank">https://policies.google.com/technologies/cookies/embedded?#types-of-cookies</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Instagram</span>
                  <a href="https://help.instagram.com/1896641480634370" target="_blank">https://help.instagram.com/1896641480634370</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Twitter</span>
                  <a href="https://help.twitter.com/en/rules-and-policies/twitter-cookies" target="_blank">https://help.twitter.com/en/rules-and-policies/twitter-cookies</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Soundcloud</span>
                  <a href="https://soundcloud.com/pages/cookies" target="_blank">https://soundcloud.com/pages/cookies</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Spotify</span>
                  <a href="https://www.spotify.com/us/legal/cookies-policy/" target="_blank">https://www.spotify.com/us/legal/cookies-policy/</a>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Vimeo</span>
                  <a href="https://vimeo.com/cookie_policy" target="_blank">https://vimeo.com/cookie_policy</a>
                </li>
              </ul>
            </div>
          </div>  
          <div :class="analyticsGroup" v-if="privacySettings.hasTrackingEnabled">
            <h2 :class="$style.groupTitle">{{ i18n.modal['analytical-title'] }}</h2>
            <p :class="$style.text">{{ i18n.modal['analytical-text'] }}</p>
            <span :class="$style.onlyConsent" v-if="privacySettings.askCookieConsent">Only with consent</span>
            <a :class="cookiesLinkClassname" data-testid="button-toggle-analytical-cookies" @click="toggleAnalyticalCookies" @keydown.enter="toggleAnalyticalCookies">{{ showAnalyticalCookies ? i18n['less-details'] : i18n['more-details'] }}</a>
            <div v-if="showAnalyticalCookies" :class="$style.cookieList">
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Cookie</span>
                  <span>initial_referrer & initial_params</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>{{ i18n.cookies.duration.session }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies.homerun }}</span>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Cookie</span>
                  <span>_ga</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>26 {{ i18n.cookies.duration.months }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies['placed-by'] }}</span>
                  <span>Google</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies['_ga'] }}</span>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Cookie</span>
                  <span>_gid</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>26 {{ i18n.cookies.duration.months }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies['placed-by'] }}</span>
                  <span>Google</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies['_gid'] }}</span>
                </li>
              </ul>
              <ul :class="$style.cookieListGroup">
                <li :class="$style.cookieListElement">
                  <span>Cookie</span>
                  <span>_gat_UA-53906462-1</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.duration.title }}</span>
                  <span>1 {{ i18n.cookies.duration.minute }}</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies['placed-by'] }}</span>
                  <span>Google</span>
                </li>
                <li :class="$style.cookieListElement">
                  <span>{{ i18n.cookies.description }}</span>
                  <span>{{ i18n.cookies['_gat'] }}</span>
                </li>
              </ul>
            </div>
          </div>        
        </div>
        <div :class="footerClassname">
          <p :class="$style.text" v-if="privacySettings.privacyNoticeURL">
            {{ i18n.modal['privacy-notice']}}
            <a
              :class="$style.link"
              :href="privacySettings.privacyNoticeURL"
              target="_blank"
              data-testid="privacy-notice-link"
            >{{ i18n.modal['privacy-notice-link'] }}</a>.</p>
          <div :class="$style.buttons" v-if="privacySettings.askCookieConsent">
            <button :class="declineClassname" data-testid="button-decline" @click="denyCookies" @keydown.enter="denyCookies">{{ i18n['btn-decline'] }}</button>
            <button :class="acceptClassname" data-testid="button-accept" @click="acceptCookies" @keydown.enter="acceptCookies">{{ i18n['btn-accept'] }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="showBanner" role="region" aria-label="Cookie Consent notice" aria-describedby="cookie-banner-description">
    <div :class="bannerClassName">
      <p :class="textClassname" id="cookie-banner-description">
        {{ noticeText }}
        <a :class="$style.link" tabindex="0" data-testid="button-open-modal" @click="openModal" @keydown.enter="openModal">{{ i18n['learn-more'] }}</a>
      </p>

      <button :class="$style.btnDismiss" @click="dismissBanner" @keydown.enter="dismissBanner" tabindex="0" v-if="!privacySettings.askCookieConsent" aria-label="Dismiss cookie banner"></button>
      <div :class="$style.buttons" v-if="privacySettings.askCookieConsent">
        <button :class="declineClassname" data-testid="button-decline" @click="denyCookies" @keydown.enter="denyCookies">{{ i18n['btn-decline'] }}</button>
        <button :class="acceptClassname" data-testid="button-accept" @click="acceptCookies" @keydown.enter="acceptCookies">{{ i18n['btn-accept'] }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import vueTypes from 'vue-types'
import { LocalStorage } from '../common/helpers'

export default {
  name: 'CookieBanner',

  props: {
    i18n: vueTypes.object,
    privacySettings: vueTypes.object,
    loadAnalyticsCookies: vueTypes.func,
  },

  data() {
    return {
      showBanner: true,
      showModal: false,
      showNecessaryCookies: false,
      showAnalyticalCookies: false,
      showFunctionalCookies: false,
    }
  },

  computed: {
    noticeText() {
      return this.privacySettings.hasTrackingEnabled ? this.i18n['analytics-notice'] : this.i18n['necessary-notice']
    },

    bannerClassName() {
      return [this.$style.content, this.privacySettings.askCookieConsent ? this.$style.contentWide : this.$style.contentNarrow]
    }, 

    declineClassname() {
      return ['btn', this.$style.btn, this.$style.btnDecline]
    },

    acceptClassname() {
      return ['btn', this.$style.btn, this.$style.btnAccept]
    },

    textClassname() {
      return [this.$style.text, this.privacySettings.askCookieConsent ? this.$style.textWithButtons : this.$style.textFullWidth]
    },

    necessaryClassnames() {
      return [this.$style.cookieGroup, this.$style.necessaryGroup]
    },

    analyticsGroup() {
      return [this.$style.cookieGroup, this.$style.analyticsGroup]
    },

    externalGroup() {
      return [this.$style.cookieGroup, this.$style.externalGroup]
    },

    footerClassname() {
      return this.privacySettings.askCookieConsent || this.privacySettings.privacyNoticeURL ? this.$style.modalFooterWithMargin : this.$style.modalFooter
    },

    cookiesLinkClassname() {
      return [this.$style.link, this.$style.cookiesLink]
    },
  },

  methods: {
    dismissBanner() {
      this.hideBanner()
      this.hideModal()
      LocalStorage.setItem('homerun-cookie-consent', {
        dismissed: true,
        timestamp: Date()
      })
    },

    acceptCookies() {
      this.hideBanner()
      this.hideModal()
      this.loadAnalyticsCookies(this.privacySettings)
      this.storeConsent(true)
    },

    denyCookies() {
      this.hideBanner()
      this.hideModal()
      this.storeConsent(false)
    },

    hideBanner() {
      this.showBanner = false
    },

    hideModal() {
      this.showModal = false
      document.body.classList.remove('modal-open')
    },

    openModal() {
      this.hideBanner()
      this.showModal = true
      document.body.classList.add('modal-open')
    },

    storeConsent(consent) {
      LocalStorage.setItem('homerun-cookie-consent', {
        confirmed: consent,
        timestamp: Date()
      })
    },

    toggleNecessaryCookies() {
      this.showNecessaryCookies = !this.showNecessaryCookies
    },

    toggleAnalyticalCookies() {
      this.showAnalyticalCookies = !this.showAnalyticalCookies
    },

    toggleFunctionalCookies() {
      this.showFunctionalCookies = !this.showFunctionalCookies
    },
  }
}
</script>

<style module>
body.modal-open{
  overflow: hidden;
  /* Keeps the width of the viewport when the scrollbar disappears */
  padding-right: 15px;
}

.content {
  display: flex;
  position: fixed;
  left: 10px;
  right: 10px;
  bottom: 10px;
  background-color: white;
  border-radius: 5px;
  z-index: 4;
  box-shadow: 0 2px 5px rgba(33, 43, 54, 0.15);
  box-sizing: border-box;
  padding: 30px;
  text-align: left;
}

.contentWide {
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.content p.text,
.modal p.text {
  color: #454F5B;
  font-family: Inter,sans-serif;
  font-size: 16px !important;
  line-height: 22px;
  margin: 0;
}

.link {
  color: #454F5B;
  text-decoration: underline;
  font-size: 16px!important;

  &:hover {
    color: #454F5B;
    text-decoration: underline !important;
  }
}

.cookiesLink {
  display: inline-block;
  margin-top: 5px;
  font-size: 14px !important;
  color: #637381;
}

.btnDismiss {
  border: none;
  padding: 0;
  cursor: pointer;
  outline: inherit;
  display: inline-block;
  padding: 0 5px;
  background: url('./assets/close.png') no-repeat;
  background-size: 100%;
  height: 16px !important;
  width: 16px !important;
  margin-top: 4px;
  display: flex;
  margin-left: auto;

  &:focus {
    border: 1px #637381 solid;
  }

  .modal & {
    display: block;
    margin-top: 0;
    float: right;
  }
}

.modalWrapper {
  position: fixed;
  z-index: 4;
  top: 0;
  right: 0;
  left: 0;
  line-height: 22px;
  background-color: rgba(3, 5, 36, .2);
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  padding: 0 20px
}

.modal {
  background-color: #FFF;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(33, 43, 54, 0.15);
  text-align: left;
  max-width: 700px;
  max-height: 82vh;
  overflow: hidden;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
}

.modalTitle {
  margin: 0 0 20px 0;
  font-weight: 800;
  font-size: 30px !important;
  line-height: 20px !important;
}

.modalHeader {
  padding: 40px 40px 20px;
  border-bottom: 1px solid #dfe3e8;
}

.modalBody {
  overflow: auto;
  padding: 20px 40px;
}

.modalFooterWithMargin {
  padding: 20px 40px;
  border-top: 1px solid #dfe3e8;
}

.modalFooter {
  margin-top: 50px;
}

.cookieGroup {
  margin-bottom: 20px;
  padding-bottom: 20px;
  background-position: 0 0;
  background-size: 24px;
  background-repeat: no-repeat;
  position: relative;
  border-bottom: 1px solid #dfe3e8;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
}

.necessaryGroup {
  background-image: url('./assets/wrench.svg');
}

.analyticsGroup {
  background-image: url('./assets/chart-line.svg');
}

.externalGroup {
  background-image: url('./assets/play-circle.svg');
}

.groupTitle {
  font-size: 16px !important;
  font-weight: 800; 
  line-height: 22px !important;
  margin: 0 0 5px 30px;
}

.onlyConsent {
  position: absolute;
  top: 0;
  right: 0;
  color: #999;
  line-height: 22px;
  font-size: 14px !important;
  font-weight: bold;
}

.cookieList {
  line-height: 22px;
  font-size: 14px !important;
  background-color: #f4f6f8;
  margin-top: 10px;
  border-radius: 4px;
}

.cookieListGroup {
  padding: 15px 10px;
  margin-bottom: 0;
  border-bottom: 1px solid;
  border-bottom-color: #dfe3e8;

  &:last-child {
    border-bottom: none;
  }
}

.cookieListElement {
  span:first-child {
    width: 100px;
    font-weight: bold;
    word-break: break-word;
    word-wrap: break-word;
  }

  span:last-child,
  a:last-child {
    flex: 1;
    word-break: break-word;
    word-wrap: break-word;
    margin-left: 8px;
  }
}

.buttons {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  margin: 20px auto 0;

  .modal & {
    float: right;
  }
}

.btn {
  font-family: 'Inter', sans-serif;
  line-height: 22px;
  font-size: 16px;
}

.btnDecline {
  color: #637381;
  background-color: #FFF;
  border-color: #DFE3E8;

  &:hover {
    color: #637381;
  }
}

.btnAccept {
  color: #FFF;
  background-color: #0192FD;
  border: 1px solid #0192FD;

  &:hover {
    color: #FFF;
    background-color: #017CD7;
  }
}

@media only screen and (min-width: 800px) {
  .content {
    left: 0;
    right: 0;
    bottom: 30px;
    width: 575px;
    margin: auto;
    text-align: left;
  }

  .contentWide {
    flex-direction: row;
    justify-content: space-between;
    gap: 30px;
    width: 775px;
  }

  .cookieGroup {
    padding-left: 40px;
    background-position: 5px 0;
  }

  .groupTitle {
    margin-left: 0;
  }

  .cookieListElement {
    display: flex;
  }

  .buttons {
    margin-top: 0;
  }

  .modal .text + .buttons {
    margin-top: 20px; 
  }
}
</style>
